import React from 'react';

import { useDeviceInfo } from 'core/hook';
import { StyleUtil } from 'ui/util';
import { HomeBusinessCardBack } from '../home-business-card-back';
import { HomeBusinessCardFront } from '../home-business-card-front';
import { HomeCustomerCardBack } from '../home-customer-card-back';
import { HomeCustomerCardFront } from '../home-customer-card-front';
import { HomeNetworkCardBack } from '../home-network-card-back';
import { HomeNetworkCardFront } from '../home-network-card-front';
import classNames from './EABD2HomeFlippingCard.module.scss';

export type HomeFlippingCardType = 'network' | 'customer' | 'business';

export interface HomeFlippingCardProps
  extends Readonly<{
    type: HomeFlippingCardType;
    onDiscoverMoreButtonPress: (type: HomeFlippingCardType) => void;
  }> {}

export const HomeFlippingCard = React.memo<HomeFlippingCardProps>((props) => {
  const { type, onDiscoverMoreButtonPress } = props;
  const { isTouchInputSupported } = useDeviceInfo();

  const [isFlipped, setFlipped] = React.useState<boolean>(() => false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const ToggleFlippedStateCallback = React.useCallback(() => setFlipped((isCurrentlyFlipped: boolean) => !isCurrentlyFlipped), []);

  const OnMouseEnterOrLeaveCallback = React.useCallback(() => {
    if (!isTouchInputSupported) {
      ToggleFlippedStateCallback();
    }
  }, [isTouchInputSupported, ToggleFlippedStateCallback]);

  const OnClickCallback = React.useCallback(() => {
    if (isTouchInputSupported) {
      ToggleFlippedStateCallback();
    }
  }, [isTouchInputSupported, ToggleFlippedStateCallback]);

  const FlipperClassNames = React.useMemo<string>(() => {
    const flipperClassNames = StyleUtil.createInitialClassNameArray(classNames.flipper);

    if (isFlipped) {
      flipperClassNames.push(classNames.flipped);
    }

    return StyleUtil.mergeClassNames(flipperClassNames);
  }, [isFlipped]);

  const OnFrontButtonClickCallback = React.useCallback(() => {
    if (rootRef && rootRef.current && rootRef.current.onclick) {
      // Note: Expected 'ev: MouseEvent' parameter but dont have any
      // @ts-ignore
      rootRef.current.onclick();
    }
  }, []);

  const FrontClassNames = React.useMemo(() => {
    const frontClassNames = StyleUtil.createInitialClassNameArray(classNames.side);

    if (!isFlipped) {
      frontClassNames.push(classNames.visible);
    }

    return StyleUtil.mergeClassNames(frontClassNames);
  }, [isFlipped]);

  const BackClassNames = React.useMemo(() => {
    const backClassNames = StyleUtil.createInitialClassNameArray(classNames.side, classNames.back);

    if (isFlipped) {
      backClassNames.push(classNames.visible);
    }

    return StyleUtil.mergeClassNames(backClassNames);
  }, [isFlipped]);

  const FrontElement = React.useMemo(() => {
    switch (type) {
      case 'network':
        return <HomeNetworkCardFront onButtonPress={OnFrontButtonClickCallback} />;
      case 'customer':
        return <HomeCustomerCardFront onButtonPress={OnFrontButtonClickCallback} />;
      case 'business':
        return <HomeBusinessCardFront onButtonPress={OnFrontButtonClickCallback} />;
      default:
        throw new Error(`Unknown flipping card type: ${type}`);
    }
  }, [type, OnFrontButtonClickCallback]);

  const BackElement = React.useMemo(() => {
    const onButtonPressCallback = () => onDiscoverMoreButtonPress(type);

    switch (type) {
      case 'network':
        return <HomeNetworkCardBack onButtonPress={onButtonPressCallback} />;
      case 'customer':
        return <HomeCustomerCardBack onButtonPress={onButtonPressCallback} />;
      case 'business':
        return <HomeBusinessCardBack onButtonPress={onButtonPressCallback} />;
      default:
        throw new Error(`Unknown flipping card type: ${type}`);
    }
  }, [type, onDiscoverMoreButtonPress]);

  return (
    <div
      ref={rootRef}
      onMouseEnter={OnMouseEnterOrLeaveCallback}
      onMouseLeave={OnMouseEnterOrLeaveCallback}
      onClick={OnClickCallback}
      className={classNames.root}>
      <div className={FlipperClassNames}>
        <div className={FrontClassNames}>{FrontElement}</div>
        <div className={BackClassNames}>{BackElement}</div>
      </div>
    </div>
  );
});
